import type { MultiLineString } from 'geojson';
import { geoCircle, geoEquirectangular, geoGraticule, geoPath } from 'd3-geo';
import {
  AU,
  BODIES,
  EARTH,
  MOON_ORBIT_RADIUS,
  isoToEpochSeconds,
  moonPosition,
  planetPosition,
  planetSemiMajorAxis,
  type PlanetName,
} from '@trajectory/astro';
import { Maximize2, Minus, Pause, Play, Plus, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCatalogLaunchSites } from '@/hooks/useCatalog';
import { useResolvedTheme } from '@/hooks/useTheme';
import { useThrottledPlayhead } from '@/hooks/useThrottledPlayhead';
import {
  groundTrackCoordinates,
  interpolateGroundTrack,
  normalizeLongitude,
  splitAtAntimeridian,
  type GroundTrackSample,
} from '@/lib/ground-track';
import {
  formatMapDistance,
  mapFrameDescription,
  moonOrbitPoints,
  nearestMapPointIndex,
  niceScaleDistance,
  planetOrbitPoints,
  subsolarPoint,
  zoomMapView,
  type MapView,
} from '@/lib/mission-map';
import { pause, play, seek } from '@/lib/playbackClock';
import { findIndexForTime, type TelemetrySeries } from '@/lib/telemetry-series';
import { loadLand } from '@/lib/worldAtlas';
import { useMissionStore } from '@/stores/missionStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useTelemetryStore } from '@/stores/telemetryStore';

const DEFAULT_VIEW: MapView = { zoom: 1, offsetX: 0, offsetY: 0 };

const THEME_COLORS = {
  dark: {
    space: '#050a12',
    oceanTop: '#0d416a',
    oceanBottom: '#071f38',
    land: '#39764a',
    landHighlight: '#5f9562',
    landShadow: '#214832',
    coastline: 'rgba(222,237,245,0.32)',
    coastGlow: 'rgba(127,209,229,0.2)',
    graticule: 'rgba(223,232,238,0.15)',
    axes: 'rgba(141,199,232,0.18)',
    site: '#ffb648',
    futureTrack: 'rgba(255,216,102,0.34)',
    track: '#ffd866',
    event: '#75d5e8',
    vehicle: '#ff7b72',
    glow: 'rgba(255,123,114,0.3)',
    label: '#aab7c7',
    lineHalo: 'rgba(0,4,10,0.72)',
  },
  light: {
    space: '#e8f0f7',
    oceanTop: '#cde8f6',
    oceanBottom: '#9fc6df',
    land: '#86b994',
    landHighlight: '#aed09f',
    landShadow: '#5e8e6a',
    coastline: 'rgba(19,25,34,0.28)',
    coastGlow: 'rgba(255,255,255,0.36)',
    graticule: 'rgba(19,25,34,0.14)',
    axes: 'rgba(49,92,128,0.2)',
    site: '#bc6800',
    futureTrack: 'rgba(139,103,0,0.3)',
    track: '#765900',
    event: '#087d95',
    vehicle: '#c8382f',
    glow: 'rgba(200,56,47,0.24)',
    label: '#4c5968',
    lineHalo: 'rgba(248,252,255,0.78)',
  },
} as const;

const CITY_LIGHTS = [
  [40.7, -74],
  [34.1, -118.2],
  [19.4, -99.1],
  [-23.6, -46.6],
  [51.5, -0.1],
  [48.9, 2.4],
  [30, 31.2],
  [6.5, 3.4],
  [28.6, 77.2],
  [19.1, 72.9],
  [23.1, 113.3],
  [31.2, 121.5],
  [39.9, 116.4],
  [37.6, 127],
  [35.7, 139.7],
  [1.3, 103.8],
  [-6.2, 106.8],
  [-33.9, 151.2],
] as const;

function formatElapsed(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const days = Math.floor(total / 86400);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const clock = `${(hours % 24).toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  return days > 0 ? `${days}d ${clock}` : clock;
}

function destinationPlanet(destinationBodyId?: string): PlanetName | null {
  return destinationBodyId === 'venus' ||
    destinationBodyId === 'mars' ||
    destinationBodyId === 'jupiter'
    ? destinationBodyId
    : null;
}

function indicesInCurrentFrame(series: TelemetrySeries, sample: GroundTrackSample): number[] {
  const frameCode = series.referenceFrameCode[sample.index];
  const bodyCode = series.centralBodyCode[sample.index];
  const indices: number[] = [];
  for (let index = 0; index < series.t.length; index += 1) {
    if (
      series.referenceFrameCode[index] === frameCode &&
      series.centralBodyCode[index] === bodyCode
    ) {
      indices.push(index);
    }
  }
  return indices;
}

function spaceFrameRadius(
  series: TelemetrySeries,
  sample: GroundTrackSample,
  destinationBodyId?: string,
): { indices: number[]; radius: number } {
  const indices = indicesInCurrentFrame(series, sample);
  let radius = 1;
  for (const index of indices) {
    radius = Math.max(radius, Math.hypot(series.positionX[index]!, series.positionY[index]!));
  }
  if (sample.referenceFrame === 'heliocentric') {
    const destination = destinationPlanet(destinationBodyId);
    radius = Math.max(
      radius,
      planetSemiMajorAxis('earth'),
      destination ? planetSemiMajorAxis(destination) : AU,
    );
  } else if (sample.referenceFrame === 'earth-centered') {
    radius = Math.max(radius, MOON_ORBIT_RADIUS);
  } else {
    radius = Math.max(radius, BODIES[sample.centralBodyId].equatorialRadius * 1.5);
  }
  return { indices, radius };
}

function drawPolyline(
  ctx: CanvasRenderingContext2D,
  points: readonly (readonly [number, number])[],
): void {
  if (points.length === 0) return;
  ctx.beginPath();
  ctx.moveTo(points[0]![0], points[0]![1]);
  for (let index = 1; index < points.length; index += 1) {
    ctx.lineTo(points[index]![0], points[index]![1]);
  }
  ctx.stroke();
}

function drawPolylineWithHalo(
  ctx: CanvasRenderingContext2D,
  points: readonly (readonly [number, number])[],
  color: string,
  width: number,
  haloColor: string,
  haloWidth = width + 3,
): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = haloColor;
  ctx.lineWidth = haloWidth;
  drawPolyline(ctx, points);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  drawPolyline(ctx, points);
  ctx.restore();
}

function solarFacing(
  latitudeDeg: number,
  longitudeDeg: number,
  sunLatitudeDeg: number,
  sunLongitudeDeg: number,
): number {
  const latitude = (latitudeDeg * Math.PI) / 180;
  const sunLatitude = (sunLatitudeDeg * Math.PI) / 180;
  const longitudeDelta = ((longitudeDeg - sunLongitudeDeg) * Math.PI) / 180;
  return (
    Math.sin(latitude) * Math.sin(sunLatitude) +
    Math.cos(latitude) * Math.cos(sunLatitude) * Math.cos(longitudeDelta)
  );
}

function drawScaleBar(
  ctx: CanvasRenderingContext2D,
  metersPerPixel: number,
  width: number,
  height: number,
  labelColor: string,
): void {
  const distance = niceScaleDistance(metersPerPixel);
  if (distance <= 0) return;
  const pixels = distance / metersPerPixel;
  const left = 14;
  const bottom = height - 18;
  ctx.save();
  ctx.strokeStyle = labelColor;
  ctx.fillStyle = labelColor;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(left, bottom - 5);
  ctx.lineTo(left, bottom);
  ctx.lineTo(left + pixels, bottom);
  ctx.lineTo(left + pixels, bottom - 5);
  ctx.stroke();
  ctx.font = '10px system-ui';
  ctx.textAlign = 'center';
  ctx.fillText(formatMapDistance(distance), left + pixels / 2, bottom - 8);
  ctx.restore();
}

/**
 * Mission map with scientifically meaningful frame changes. Earth phases use
 * a rotating ground track and real solar terminator; coast phases use inertial
 * projections with ephemeris-driven reference orbits and distances to scale.
 */
export function Map2DPanel() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
    origin: MapView;
    moved: boolean;
  } | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [view, setView] = useState<MapView>(DEFAULT_VIEW);
  const [cursorLabel, setCursorLabel] = useState<string | null>(null);
  const launchSites = useCatalogLaunchSites();
  const missionLaunchSiteId = useMissionStore((state) => state.document?.launchSiteId);
  const missionLaunchAt = useMissionStore((state) => state.document?.launchAt);
  const status = useTelemetryStore((state) => state.status);
  const events = useTelemetryStore((state) => state.events);
  const summary = useTelemetryStore((state) => state.summary);
  const reducedMotion = useSettingsStore((state) => state.reducedMotion);
  const { time, duration, playing, speed } = useThrottledPlayhead(reducedMotion ? 10 : 30);
  const resolvedTheme = useResolvedTheme();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const series = status === 'ready' ? useTelemetryStore.getState().series : null;
  const sample = series ? interpolateGroundTrack(series, time) : null;
  const latestEvent = [...events].reverse().find((event) => event.t <= time);
  const missionDestination = destinationPlanet(summary?.destinationBodyId);
  const referenceOrbitEpoch = series?.epoch[0] ?? null;
  const referenceOrbits = useMemo(
    () => ({
      earth: referenceOrbitEpoch === null ? [] : planetOrbitPoints('earth', referenceOrbitEpoch),
      destination:
        referenceOrbitEpoch === null || !missionDestination
          ? []
          : planetOrbitPoints(missionDestination, referenceOrbitEpoch),
      moon: referenceOrbitEpoch === null ? [] : moonOrbitPoints(referenceOrbitEpoch),
    }),
    [missionDestination, referenceOrbitEpoch],
  );
  const frameKey = sample
    ? `${sample.referenceFrame}:${sample.centralBodyId}`
    : 'earth-inertial:earth';

  useEffect(() => {
    setView(DEFAULT_VIEW);
    setCursorLabel(null);
  }, [frameKey]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.width === 0 || size.height === 0) return;

    // Preserve fine coastlines and trajectory edges on modern high-density
    // displays while retaining a finite ceiling for very large dock layouts.
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    canvas.width = Math.round(size.width * dpr);
    canvas.height = Math.round(size.height * dpr);
    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);

    const colors = THEME_COLORS[resolvedTheme];
    const centreX = size.width / 2 + view.offsetX;
    const centreY = size.height / 2 + view.offsetY;

    if (series && sample && sample.referenceFrame !== 'earth-inertial') {
      const padding = 46;
      const { indices, radius } = spaceFrameRadius(series, sample, summary?.destinationBodyId);
      const basePixelsPerMeter =
        Math.max(1, Math.min(size.width, size.height) / 2 - padding) / Math.max(1, radius);
      const pixelsPerMeter = basePixelsPerMeter * view.zoom;
      const projectSpace = ([x, y]: readonly [number, number]): [number, number] => [
        centreX + x * pixelsPerMeter,
        centreY - y * pixelsPerMeter,
      ];

      const spaceGradient = ctx.createRadialGradient(
        centreX,
        centreY,
        0,
        centreX,
        centreY,
        Math.max(size.width, size.height) * 0.75,
      );
      spaceGradient.addColorStop(0, resolvedTheme === 'dark' ? '#10233a' : '#f3f8fc');
      spaceGradient.addColorStop(0.48, colors.space);
      spaceGradient.addColorStop(1, resolvedTheme === 'dark' ? '#02050b' : '#dce8f2');
      ctx.fillStyle = spaceGradient;
      ctx.fillRect(0, 0, size.width, size.height);
      ctx.fillStyle = resolvedTheme === 'dark' ? 'rgba(255,255,255,0.42)' : 'rgba(18,39,62,0.16)';
      for (let star = 0; star < 190; star += 1) {
        const x = (star * 97.37 + 11) % size.width;
        const y = (star * star * 19.13 + 31) % size.height;
        const starSize = star % 17 === 0 ? 1.7 : star % 7 === 0 ? 1.1 : 0.65;
        ctx.globalAlpha = 0.45 + ((star * 37) % 55) / 100;
        ctx.fillRect(x, y, starSize, starSize);
      }
      ctx.globalAlpha = 1;

      ctx.strokeStyle = colors.axes;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 5]);
      ctx.beginPath();
      ctx.moveTo(0, centreY);
      ctx.lineTo(size.width, centreY);
      ctx.moveTo(centreX, 0);
      ctx.lineTo(centreX, size.height);
      ctx.stroke();
      ctx.setLineDash([]);

      // Concentric range rings make inertial views quantitatively readable
      // even before the user reaches for the cursor or scale bar.
      ctx.save();
      ctx.strokeStyle = colors.axes;
      ctx.fillStyle = colors.label;
      ctx.font = '9px system-ui';
      ctx.textAlign = 'left';
      for (const fraction of [0.25, 0.5, 0.75, 1]) {
        const ringRadius = radius * fraction * pixelsPerMeter;
        if (ringRadius < 18 || ringRadius > Math.max(size.width, size.height) * 1.8) {
          continue;
        }
        ctx.globalAlpha = fraction === 1 ? 0.62 : 0.42;
        ctx.setLineDash(fraction === 1 ? [5, 5] : [2, 5]);
        ctx.beginPath();
        ctx.arc(centreX, centreY, ringRadius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillText(formatMapDistance(radius * fraction), centreX + 5, centreY - ringRadius + 12);
      }
      ctx.restore();

      const epoch = sample.epoch;
      const drawReferenceOrbit = (
        points: readonly (readonly [number, number])[],
        color: string,
      ) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 4]);
        drawPolyline(ctx, points.map(projectSpace));
        ctx.setLineDash([]);
      };

      if (sample.referenceFrame === 'heliocentric') {
        drawReferenceOrbit(
          referenceOrbits.earth,
          resolvedTheme === 'dark' ? 'rgba(95,168,232,0.35)' : 'rgba(30,102,164,0.35)',
        );
        if (missionDestination) {
          drawReferenceOrbit(
            referenceOrbits.destination,
            resolvedTheme === 'dark' ? 'rgba(255,155,102,0.38)' : 'rgba(175,71,22,0.35)',
          );
        }

        const sunPoint = projectSpace([0, 0]);
        const sunGradient = ctx.createRadialGradient(
          sunPoint[0],
          sunPoint[1],
          0,
          sunPoint[0],
          sunPoint[1],
          24,
        );
        sunGradient.addColorStop(0, '#fffbd1');
        sunGradient.addColorStop(0.28, '#ffd866');
        sunGradient.addColorStop(1, 'rgba(255,184,72,0)');
        ctx.fillStyle = sunGradient;
        ctx.beginPath();
        ctx.arc(sunPoint[0], sunPoint[1], 24, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff2a0';
        ctx.beginPath();
        ctx.arc(sunPoint[0], sunPoint[1], 5, 0, Math.PI * 2);
        ctx.fill();

        const planets: { name: PlanetName; color: string }[] = [
          { name: 'earth', color: '#5fa8e8' },
          ...(missionDestination ? [{ name: missionDestination, color: '#ff9b66' }] : []),
        ];
        for (const planet of planets) {
          const position = planetPosition(planet.name, epoch);
          const point = projectSpace([position[0], position[1]]);
          ctx.fillStyle = planet.color;
          ctx.beginPath();
          ctx.arc(point[0], point[1], planet.name === 'earth' ? 4 : 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = colors.label;
          ctx.font = '10px system-ui';
          ctx.fillText(
            planet.name[0]!.toUpperCase() + planet.name.slice(1),
            point[0] + 7,
            point[1] - 5,
          );
        }
      } else if (sample.referenceFrame === 'earth-centered') {
        drawReferenceOrbit(
          referenceOrbits.moon,
          resolvedTheme === 'dark' ? 'rgba(199,203,209,0.34)' : 'rgba(74,81,92,0.32)',
        );
        const earthPoint = projectSpace([0, 0]);
        const earthRadius = Math.max(5, EARTH.equatorialRadius * pixelsPerMeter);
        ctx.fillStyle = '#3d91d0';
        ctx.beginPath();
        ctx.arc(earthPoint[0], earthPoint[1], earthRadius, 0, Math.PI * 2);
        ctx.fill();
        const moon = moonPosition(epoch);
        const moonPoint = projectSpace([moon[0], moon[1]]);
        ctx.fillStyle = '#c7cbd1';
        ctx.beginPath();
        ctx.arc(moonPoint[0], moonPoint[1], 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = colors.label;
        ctx.font = '10px system-ui';
        ctx.fillText('Moon', moonPoint[0] + 7, moonPoint[1] - 5);
      } else {
        const body = BODIES[sample.centralBodyId];
        const bodyPoint = projectSpace([0, 0]);
        const bodyRadius = Math.max(8, body.equatorialRadius * pixelsPerMeter);
        const gradient = ctx.createRadialGradient(
          bodyPoint[0] - bodyRadius * 0.3,
          bodyPoint[1] - bodyRadius * 0.3,
          0,
          bodyPoint[0],
          bodyPoint[1],
          bodyRadius,
        );
        gradient.addColorStop(0, '#b5cadb');
        gradient.addColorStop(1, '#526b82');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(bodyPoint[0], bodyPoint[1], bodyRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = colors.label;
        ctx.font = '10px system-ui';
        ctx.fillText(
          sample.centralBodyId[0]!.toUpperCase() + sample.centralBodyId.slice(1),
          bodyPoint[0] + bodyRadius + 5,
          bodyPoint[1] - 4,
        );
      }

      const trajectoryPoints = indices.map((index) =>
        projectSpace([series.positionX[index]!, series.positionY[index]!]),
      );
      ctx.save();
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = colors.lineHalo;
      ctx.lineWidth = 3.8;
      drawPolyline(ctx, trajectoryPoints);
      ctx.strokeStyle = colors.futureTrack;
      ctx.lineWidth = 1.35;
      drawPolyline(ctx, trajectoryPoints);
      ctx.restore();

      const traveledPoints: [number, number][] = [];
      for (let offset = 0; offset < indices.length; offset += 1) {
        if (indices[offset]! > sample.index) break;
        traveledPoints.push(trajectoryPoints[offset]!);
      }
      const nextIndex = Math.min(sample.index + 1, series.t.length - 1);
      if (
        sample.fraction > 0 &&
        series.referenceFrameCode[nextIndex] === series.referenceFrameCode[sample.index] &&
        series.centralBodyCode[nextIndex] === series.centralBodyCode[sample.index]
      ) {
        traveledPoints.push(
          projectSpace([
            series.positionX[sample.index]! +
              (series.positionX[nextIndex]! - series.positionX[sample.index]!) * sample.fraction,
            series.positionY[sample.index]! +
              (series.positionY[nextIndex]! - series.positionY[sample.index]!) * sample.fraction,
          ]),
        );
      }
      drawPolylineWithHalo(ctx, traveledPoints, colors.track, 2.6, colors.lineHalo, 5.8);

      for (const event of events) {
        const index = findIndexForTime(series.t, event.t);
        if (
          index < 0 ||
          series.referenceFrameCode[index] !== series.referenceFrameCode[sample.index] ||
          series.centralBodyCode[index] !== series.centralBodyCode[sample.index]
        ) {
          continue;
        }
        const point = projectSpace([series.positionX[index]!, series.positionY[index]!]);
        ctx.save();
        ctx.translate(point[0], point[1]);
        ctx.rotate(Math.PI / 4);
        const occurred = event.t <= time;
        ctx.shadowColor = occurred ? colors.event : 'transparent';
        ctx.shadowBlur = occurred ? 7 : 0;
        ctx.fillStyle = occurred ? colors.event : colors.futureTrack;
        ctx.fillRect(-3.5, -3.5, 7, 7);
        ctx.strokeStyle = colors.lineHalo;
        ctx.lineWidth = 1;
        ctx.strokeRect(-3.5, -3.5, 7, 7);
        ctx.restore();
        if (event === latestEvent && event.t <= time) {
          ctx.fillStyle = colors.label;
          ctx.font = '10px system-ui';
          ctx.fillText(event.label, point[0] + 8, point[1] - 8);
        }
      }

      const currentPosition =
        traveledPoints.at(-1) ??
        projectSpace([series.positionX[sample.index]!, series.positionY[sample.index]!]);
      ctx.strokeStyle = colors.axes;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(centreX, centreY);
      ctx.lineTo(currentPosition[0], currentPosition[1]);
      ctx.stroke();
      ctx.fillStyle = colors.glow;
      ctx.beginPath();
      ctx.arc(
        currentPosition[0],
        currentPosition[1],
        11 + (playing && !reducedMotion ? Math.sin(performance.now() / 180) * 2 : 0),
        0,
        Math.PI * 2,
      );
      ctx.fill();
      ctx.strokeStyle = colors.vehicle;
      ctx.globalAlpha = 0.45;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(currentPosition[0] - 9, currentPosition[1]);
      ctx.lineTo(currentPosition[0] + 9, currentPosition[1]);
      ctx.moveTo(currentPosition[0], currentPosition[1] - 9);
      ctx.lineTo(currentPosition[0], currentPosition[1] + 9);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.fillStyle = colors.vehicle;
      ctx.beginPath();
      ctx.arc(currentPosition[0], currentPosition[1], 4.5, 0, Math.PI * 2);
      ctx.fill();

      drawScaleBar(ctx, 1 / pixelsPerMeter, size.width, size.height, colors.label);
      return;
    }

    const projection = geoEquirectangular().fitSize([size.width, size.height], {
      type: 'Sphere',
    });
    projection.scale(projection.scale() * view.zoom).translate([centreX, centreY]);
    const path = geoPath(projection, ctx);
    const displayEpoch =
      series && sample
        ? series.epoch[sample.index]!
        : isoToEpochSeconds(missionLaunchAt ?? new Date().toISOString());
    const sun = subsolarPoint(displayEpoch);

    ctx.fillStyle = resolvedTheme === 'dark' ? '#06111c' : '#dce8ef';
    ctx.fillRect(0, 0, size.width, size.height);
    const oceanGradient = ctx.createLinearGradient(0, 0, 0, size.height);
    oceanGradient.addColorStop(0, colors.oceanTop);
    oceanGradient.addColorStop(1, colors.oceanBottom);
    ctx.fillStyle = oceanGradient;
    ctx.beginPath();
    path({ type: 'Sphere' });
    ctx.fill();
    ctx.strokeStyle = colors.coastGlow;
    ctx.lineWidth = 5;
    ctx.beginPath();
    path({ type: 'Sphere' });
    ctx.stroke();

    ctx.strokeStyle = colors.graticule;
    ctx.lineWidth = 1;
    ctx.beginPath();
    path(geoGraticule().step(view.zoom >= 3 ? [10, 10] : [30, 30])());
    ctx.stroke();

    const landGradient = ctx.createLinearGradient(0, 0, 0, size.height);
    landGradient.addColorStop(0, colors.landHighlight);
    landGradient.addColorStop(0.22, colors.land);
    landGradient.addColorStop(0.52, colors.landShadow);
    landGradient.addColorStop(0.78, colors.land);
    landGradient.addColorStop(1, colors.landHighlight);
    ctx.save();
    ctx.shadowColor = resolvedTheme === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(26,58,77,0.24)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetY = 1.5;
    ctx.fillStyle = landGradient;
    ctx.beginPath();
    path(loadLand());
    ctx.fill();
    ctx.restore();
    ctx.strokeStyle = colors.coastGlow;
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    path(loadLand());
    ctx.stroke();
    ctx.strokeStyle = colors.coastline;
    ctx.lineWidth = 0.75;
    ctx.beginPath();
    path(loadLand());
    ctx.stroke();

    const antisolarPoint: [number, number] = [
      normalizeLongitude(sun.longitudeDeg + 180),
      -sun.latitudeDeg,
    ];
    const twilightBands =
      resolvedTheme === 'dark'
        ? [
            { radius: 90, color: 'rgba(4,10,25,0.27)' },
            { radius: 84, color: 'rgba(1,5,18,0.24)' },
            { radius: 78, color: 'rgba(0,2,10,0.3)' },
          ]
        : [
            { radius: 90, color: 'rgba(13,35,61,0.14)' },
            { radius: 84, color: 'rgba(8,25,49,0.1)' },
            { radius: 78, color: 'rgba(4,17,37,0.12)' },
          ];
    for (const band of twilightBands) {
      const region = geoCircle().center(antisolarPoint).radius(band.radius).precision(1.5)();
      ctx.fillStyle = band.color;
      ctx.beginPath();
      path(region);
      ctx.fill();
    }
    const nightHemisphere = geoCircle().center(antisolarPoint).radius(90).precision(1.5)();
    ctx.strokeStyle =
      resolvedTheme === 'dark' ? 'rgba(126,192,232,0.25)' : 'rgba(255,255,255,0.58)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    path(nightHemisphere);
    ctx.stroke();

    if (view.zoom >= 0.85) {
      for (const [latitude, longitude] of CITY_LIGHTS) {
        if (solarFacing(latitude, longitude, sun.latitudeDeg, sun.longitudeDeg) > -0.04) {
          continue;
        }
        const point = projection([longitude, latitude]);
        if (!point) continue;
        const glow = ctx.createRadialGradient(
          point[0],
          point[1],
          0,
          point[0],
          point[1],
          view.zoom >= 2 ? 5 : 3,
        );
        glow.addColorStop(0, 'rgba(255,232,154,0.92)');
        glow.addColorStop(0.28, 'rgba(255,181,79,0.45)');
        glow.addColorStop(1, 'rgba(255,154,54,0)');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(point[0], point[1], view.zoom >= 2 ? 5 : 3, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const subsolar = projection([sun.longitudeDeg, sun.latitudeDeg]);
    if (subsolar) {
      const solarGlow = ctx.createRadialGradient(
        subsolar[0],
        subsolar[1],
        0,
        subsolar[0],
        subsolar[1],
        11,
      );
      solarGlow.addColorStop(0, 'rgba(255,243,177,0.8)');
      solarGlow.addColorStop(0.25, 'rgba(255,216,102,0.3)');
      solarGlow.addColorStop(1, 'rgba(255,216,102,0)');
      ctx.fillStyle = solarGlow;
      ctx.beginPath();
      ctx.arc(subsolar[0], subsolar[1], 11, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,244,192,0.94)';
      ctx.beginPath();
      ctx.arc(subsolar[0], subsolar[1], 2.2, 0, Math.PI * 2);
      ctx.fill();
    }

    for (const site of launchSites.data ?? []) {
      const projected = projection([site.longitudeDeg, site.latitudeDeg]);
      if (!projected) continue;
      const isCurrent = site.id === missionLaunchSiteId;
      if (isCurrent) {
        ctx.beginPath();
        ctx.arc(projected[0], projected[1], 7, 0, Math.PI * 2);
        ctx.strokeStyle = colors.site;
        ctx.globalAlpha = 0.55;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      ctx.beginPath();
      ctx.arc(projected[0], projected[1], isCurrent ? 3.5 : 2, 0, Math.PI * 2);
      ctx.fillStyle = colors.site;
      ctx.fill();
      if (isCurrent && view.zoom >= 1.5) {
        ctx.fillStyle = colors.label;
        ctx.font = '10px system-ui';
        ctx.fillText(site.name, projected[0] + 7, projected[1] - 5);
      }
    }

    if (series && sample) {
      const fullTrack: MultiLineString = {
        type: 'MultiLineString',
        coordinates: splitAtAntimeridian(groundTrackCoordinates(series)),
      };
      ctx.save();
      ctx.strokeStyle = colors.lineHalo;
      ctx.lineWidth = 3.8;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      path(fullTrack);
      ctx.stroke();
      ctx.strokeStyle = colors.futureTrack;
      ctx.lineWidth = 1.25;
      ctx.beginPath();
      path(fullTrack);
      ctx.stroke();
      ctx.restore();

      const traveledCoordinates = groundTrackCoordinates(series, sample.earthTrackIndex);
      if (!sample.offEarth) {
        traveledCoordinates.push([sample.longitudeDeg, sample.latitudeDeg]);
      }
      const traveledTrack: MultiLineString = {
        type: 'MultiLineString',
        coordinates: splitAtAntimeridian(traveledCoordinates),
      };
      ctx.strokeStyle = colors.lineHalo;
      ctx.lineWidth = 5.8;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      path(traveledTrack);
      ctx.stroke();
      ctx.strokeStyle = colors.track;
      ctx.lineWidth = 2.6;
      ctx.beginPath();
      path(traveledTrack);
      ctx.stroke();

      for (const event of events) {
        const index = findIndexForTime(series.t, event.t);
        if (
          index < 0 ||
          series.centralBodyCode[index] !== 0 ||
          series.referenceFrameCode[index] !== 0
        ) {
          continue;
        }
        const point = projection([series.longitudeDeg[index]!, series.latitudeDeg[index]!]);
        if (!point) continue;
        ctx.save();
        ctx.translate(point[0], point[1]);
        ctx.rotate(Math.PI / 4);
        const occurred = event.t <= time;
        ctx.shadowColor = occurred ? colors.event : 'transparent';
        ctx.shadowBlur = occurred ? 7 : 0;
        ctx.fillStyle = occurred ? colors.event : colors.futureTrack;
        ctx.fillRect(-3.5, -3.5, 7, 7);
        ctx.strokeStyle = colors.lineHalo;
        ctx.lineWidth = 1;
        ctx.strokeRect(-3.5, -3.5, 7, 7);
        ctx.restore();
        if (event === latestEvent && occurred) {
          ctx.fillStyle = colors.label;
          ctx.font = '10px system-ui';
          ctx.fillText(event.label, point[0] + 8, point[1] - 8);
        }
      }

      const markerLongitude = sample.offEarth
        ? series.longitudeDeg[sample.earthTrackIndex]!
        : sample.longitudeDeg;
      const markerLatitude = sample.offEarth
        ? series.latitudeDeg[sample.earthTrackIndex]!
        : sample.latitudeDeg;
      const here = projection([markerLongitude, markerLatitude]);
      if (here && !sample.offEarth) {
        const pulse = playing && !reducedMotion ? (Math.sin(performance.now() / 180) + 1) / 2 : 0;
        ctx.beginPath();
        ctx.arc(here[0], here[1], 8 + pulse * 5, 0, Math.PI * 2);
        ctx.fillStyle = colors.glow;
        ctx.fill();

        const previousIndex = Math.max(0, sample.index - 1);
        const previous = projection([
          series.longitudeDeg[previousIndex]!,
          series.latitudeDeg[previousIndex]!,
        ]);
        const dx = previous ? here[0] - previous[0] : 0;
        const dy = previous ? here[1] - previous[1] : -1;
        const heading =
          previous && Math.abs(dx) < size.width / 2 && Math.hypot(dx, dy) > 0.2
            ? Math.atan2(dy, dx) + Math.PI / 2
            : 0;

        ctx.save();
        ctx.translate(here[0], here[1]);
        ctx.rotate(heading);
        ctx.beginPath();
        ctx.moveTo(0, -7);
        ctx.lineTo(5, 5);
        ctx.lineTo(0, 3);
        ctx.lineTo(-5, 5);
        ctx.closePath();
        ctx.fillStyle = colors.vehicle;
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      }
    }

    drawScaleBar(
      ctx,
      EARTH.equatorialRadius / projection.scale(),
      size.width,
      size.height,
      colors.label,
    );
  }, [
    size,
    launchSites.data,
    missionLaunchSiteId,
    missionLaunchAt,
    resolvedTheme,
    series,
    sample,
    summary,
    missionDestination,
    referenceOrbits,
    events,
    playing,
    reducedMotion,
    time,
    view,
  ]);

  const updateCursor = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas || size.width === 0 || size.height === 0) return;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const centreX = size.width / 2 + view.offsetX;
    const centreY = size.height / 2 + view.offsetY;

    if (series && sample && sample.referenceFrame !== 'earth-inertial') {
      const { radius } = spaceFrameRadius(series, sample, summary?.destinationBodyId);
      const basePixelsPerMeter =
        Math.max(1, Math.min(size.width, size.height) / 2 - 46) / Math.max(1, radius);
      const pixelsPerMeter = basePixelsPerMeter * view.zoom;
      const mapX = (x - centreX) / pixelsPerMeter;
      const mapY = (centreY - y) / pixelsPerMeter;
      setCursorLabel(
        `X ${formatMapDistance(Math.abs(mapX))} ${mapX < 0 ? 'W' : 'E'} · Y ${formatMapDistance(Math.abs(mapY))} ${mapY < 0 ? 'S' : 'N'}`,
      );
      return;
    }

    const projection = geoEquirectangular().fitSize([size.width, size.height], {
      type: 'Sphere',
    });
    projection.scale(projection.scale() * view.zoom).translate([centreX, centreY]);
    const coordinate = projection.invert?.([x, y]);
    if (!coordinate) {
      setCursorLabel(null);
      return;
    }
    const longitude = normalizeLongitude(coordinate[0]);
    const latitude = Math.max(-90, Math.min(90, coordinate[1]));
    setCursorLabel(
      `${Math.abs(latitude).toFixed(2)}°${latitude >= 0 ? 'N' : 'S'} · ${Math.abs(longitude).toFixed(2)}°${longitude >= 0 ? 'E' : 'W'}`,
    );
  };

  const seekNearestTrajectoryPoint = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas || !series || !sample) return;
    const rect = canvas.getBoundingClientRect();
    const pointerX = clientX - rect.left;
    const pointerY = clientY - rect.top;
    const centreX = size.width / 2 + view.offsetX;
    const centreY = size.height / 2 + view.offsetY;
    let candidates: { index: number; point: readonly [number, number] }[] = [];

    if (sample.referenceFrame !== 'earth-inertial') {
      const { indices, radius } = spaceFrameRadius(series, sample, summary?.destinationBodyId);
      const pixelsPerMeter =
        (Math.max(1, Math.min(size.width, size.height) / 2 - 46) / Math.max(1, radius)) * view.zoom;
      candidates = indices.map((index) => ({
        index,
        point: [
          centreX + series.positionX[index]! * pixelsPerMeter,
          centreY - series.positionY[index]! * pixelsPerMeter,
        ],
      }));
    } else {
      const projection = geoEquirectangular().fitSize([size.width, size.height], {
        type: 'Sphere',
      });
      projection.scale(projection.scale() * view.zoom).translate([centreX, centreY]);
      const stride = Math.max(1, Math.ceil(series.t.length / 2500));
      for (let index = 0; index < series.t.length; index += stride) {
        if (series.centralBodyCode[index] !== 0 || series.referenceFrameCode[index] !== 0) {
          continue;
        }
        const point = projection([series.longitudeDeg[index]!, series.latitudeDeg[index]!]);
        if (point) candidates.push({ index, point });
      }
    }

    const nearest = nearestMapPointIndex(
      candidates.map((candidate) => candidate.point),
      pointerX,
      pointerY,
    );
    if (nearest >= 0) {
      pause();
      seek(series.t[candidates[nearest]!.index]!);
    }
  };

  const changeZoom = (factor: number) => {
    setView((current) =>
      zoomMapView(
        current,
        current.zoom * factor,
        size.width / 2,
        size.height / 2,
        size.width,
        size.height,
      ),
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden bg-surface-sunken"
      data-testid="mission-map"
    >
      <canvas
        ref={canvasRef}
        aria-label="Animated mission ground-track map"
        title="Drag to pan, scroll to zoom, or select the trajectory to seek"
        className="cursor-grab touch-none active:cursor-grabbing"
        onDoubleClick={() => setView(DEFAULT_VIEW)}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          dragRef.current = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            origin: view,
            moved: false,
          };
        }}
        onPointerMove={(event) => {
          updateCursor(event.clientX, event.clientY);
          const drag = dragRef.current;
          if (!drag || drag.pointerId !== event.pointerId) return;
          const dx = event.clientX - drag.x;
          const dy = event.clientY - drag.y;
          if (Math.hypot(dx, dy) > 3) drag.moved = true;
          setView({
            ...drag.origin,
            offsetX: drag.origin.offsetX + dx,
            offsetY: drag.origin.offsetY + dy,
          });
        }}
        onPointerUp={(event) => {
          const drag = dragRef.current;
          if (drag?.pointerId === event.pointerId && !drag.moved) {
            seekNearestTrajectoryPoint(event.clientX, event.clientY);
          }
          dragRef.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onPointerCancel={() => {
          dragRef.current = null;
        }}
        onPointerLeave={() => {
          if (!dragRef.current) setCursorLabel(null);
        }}
        onWheel={(event) => {
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          setView((current) =>
            zoomMapView(
              current,
              event.deltaY < 0 ? current.zoom * 1.25 : current.zoom / 1.25,
              event.clientX - rect.left,
              event.clientY - rect.top,
              size.width,
              size.height,
            ),
          );
        }}
      />

      <div className="absolute right-3 top-3 flex flex-col overflow-hidden rounded-control border border-border bg-surface-raised/90 shadow-lg backdrop-blur-sm">
        <button
          type="button"
          onClick={() => changeZoom(1.4)}
          title="Zoom in"
          aria-label="Zoom mission map in"
          className="flex h-8 w-8 items-center justify-center text-muted hover:bg-surface-sunken hover:text-text"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => changeZoom(1 / 1.4)}
          title="Zoom out"
          aria-label="Zoom mission map out"
          className="flex h-8 w-8 items-center justify-center border-y border-border text-muted hover:bg-surface-sunken hover:text-text"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setView(DEFAULT_VIEW)}
          title="Fit mission to map"
          aria-label="Fit mission map to view"
          className="flex h-8 w-8 items-center justify-center text-muted hover:bg-surface-sunken hover:text-text"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {status === 'idle' && (
        <div className="pointer-events-none absolute inset-x-4 bottom-4 rounded-panel border border-border bg-surface-raised/90 px-3 py-2 text-center text-xs text-muted shadow-lg backdrop-blur-sm">
          Run a simulation from Telemetry to explore its Earth track and deep-space trajectory.
        </div>
      )}

      {status === 'running' && (
        <div className="pointer-events-none absolute left-1/2 top-4 -translate-x-1/2 rounded-full border border-border bg-surface-raised/90 px-3 py-1.5 text-xs text-info shadow-lg backdrop-blur-sm">
          Computing deterministic trajectory…
        </div>
      )}

      {status === 'ready' && sample && (
        <>
          <div className="absolute left-3 top-3 max-w-[calc(100%-4.5rem)] rounded-panel border border-border bg-surface-raised/90 p-2 text-xs shadow-lg backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => (playing ? pause() : play())}
                title={playing ? 'Pause map playback' : 'Play map playback'}
                aria-label={playing ? 'Pause map playback' : 'Play map playback'}
                className="flex h-7 w-7 items-center justify-center rounded-control bg-accent text-accent-contrast hover:brightness-110"
              >
                {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={() => {
                  pause();
                  seek(0);
                }}
                title="Return to launch"
                aria-label="Return map playback to launch"
                className="flex h-7 w-7 items-center justify-center rounded-control border border-border text-muted hover:bg-surface-sunken hover:text-text"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
              <span className="tabular-nums text-text">{formatElapsed(time)}</span>
              <span className="text-faint">/ {formatElapsed(duration)}</span>
              <span className="rounded-full bg-surface-sunken px-1.5 py-0.5 text-[10px] text-muted">
                {speed}×
              </span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 tabular-nums">
              <span className="text-faint">
                {sample.referenceFrame === 'heliocentric' ? 'Solar radius' : 'Altitude'}
              </span>
              <span className="text-right text-text">
                {(sample.altitudeMeters / 1000).toFixed(1)} km
              </span>
              <span className="text-faint">Speed</span>
              <span className="text-right text-text">
                {(sample.speedMetersPerSecond / 1000).toFixed(2)} km/s
              </span>
              <span className="text-faint">Phase</span>
              <span className="text-right capitalize text-text">
                {sample.phase.replaceAll('-', ' ')}
              </span>
            </div>
            <p className="mt-2 border-t border-border pt-1.5 text-[10px] text-faint">
              {mapFrameDescription(sample.referenceFrame, sample.centralBodyId)}
            </p>
          </div>

          {latestEvent && (
            <div
              aria-live="polite"
              className="absolute right-14 top-3 hidden max-w-52 rounded-panel border border-info/30 bg-surface-raised/90 px-3 py-2 text-xs shadow-lg backdrop-blur-sm min-[680px]:block"
            >
              <span className="block text-[10px] uppercase tracking-wide text-info">
                Latest mission event
              </span>
              <strong className="mt-0.5 block text-text">{latestEvent.label}</strong>
              {latestEvent.detail && (
                <span className="mt-0.5 block leading-relaxed text-faint">
                  {latestEvent.detail}
                </span>
              )}
            </div>
          )}

          <div className="pointer-events-none absolute bottom-5 right-3 hidden rounded-control border border-border bg-surface-raised/80 px-2 py-1.5 text-[10px] text-muted shadow backdrop-blur-sm min-[440px]:block">
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-5 bg-warning" />
              Flown
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="inline-block w-5 border-t border-dashed border-warning/50" />
              Planned
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="inline-block h-1.5 w-1.5 rotate-45 bg-info" />
              Mission event
            </div>
          </div>

          {cursorLabel && (
            <div className="pointer-events-none absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full border border-border bg-surface-raised/85 px-2.5 py-1 text-[10px] tabular-nums text-muted shadow backdrop-blur-sm">
              {cursorLabel}
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 h-1 bg-surface-sunken">
            <div
              className="h-full bg-accent transition-[width] duration-75"
              style={{ width: `${duration > 0 ? Math.min(100, (time / duration) * 100) : 0}%` }}
            />
          </div>
        </>
      )}
    </div>
  );
}
